import React from "react";

/**
 * Props accepted by Pagination Component
 */
interface PaginationProps {
  page: number;
  totalPages: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  hasPrevPage?: boolean;
  hasNextPage?: boolean;
  className?: string;
}

/**
 * Reusable Pagination Component
 *
 * Features:
 * - Shows max 5 page numbers at a time
 * - Centers current page when possible
 * - Handles edge cases (start & end pages)
 * - Bootstrap pagination compatible
 * - Fully controlled via parent state
 *
 * Example:
 * <Pagination
 *   page={page}
 *   totalPages={totalPages}
 *   setPage={setPage}
 *   hasPrevPage={hasPrevPage}
 *   hasNextPage={hasNextPage}
 * />
 */
const Pagination: React.FC<PaginationProps> = ({
  page,
  totalPages,
  setPage,
  hasPrevPage = false,
  hasNextPage = false,
  className = "",
}) => {

  /**
   * Navigate to next page
   * Only works if next page exists
   */
  const goToNextPage = () => {
    if (hasNextPage) {
      setPage((prev) => prev + 1);
    }
  };

  /**
   * Navigate to previous page
   * Only works if previous page exists
   */
  const goToPrevPage = () => {
    if (hasPrevPage) {
      setPage((prev) => prev - 1);
    }
  };

  /**
   * Navigate directly to selected page
   */
  const goToPage = (pageNumber: number) => {
    setPage(pageNumber);
  };

  /**
   * Calculate visible page numbers
   *
   * Logic:
   * - Show max 5 pages at once
   * - Try to keep current page centered
   * - Adjust when near start or end
   */
  const getVisiblePages = () => {
    const maxPagesToShow = 5;

    // If total pages less than max → show all
    if (totalPages <= maxPagesToShow) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    let start = page - 2;
    let end = page + 2;

    // Near start adjustment
    if (start < 1) {
      start = 1;
      end = maxPagesToShow;
    }

    // Near end adjustment
    if (end > totalPages) {
      end = totalPages;
      start = totalPages - maxPagesToShow + 1;
    }

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const visiblePages = getVisiblePages();

  return (
    <div className={`row align-items-center mt-4 ${className}`}>
      
      {/* Page Info */}
      <div className="col-md-2">
        <p className="pagination-text">
          Page {page} of {totalPages}
        </p>
      </div>

      {/* Pagination Buttons */}
      <div className="col-md-10">
        <ul className="pagination lms-page justify-content-center justify-content-md-end">

          {/* Previous Button */}
          <li className={`page-item ${!hasPrevPage ? "disabled" : ""}`}>
            <button className="page-link" onClick={goToPrevPage}>
              <i className="fas fa-angle-left" />
            </button>
          </li>

          {/* Page Numbers */}
          {visiblePages.map((p) => (
            <li key={p} className={`page-item ${p === page ? "active" : ""}`}>
              <button className="page-link" onClick={() => goToPage(p)}>
                {p}
              </button>
            </li>
          ))}

          {/* Next Button */}
          <li className={`page-item ${!hasNextPage ? "disabled" : ""}`}>
            <button className="page-link" onClick={goToNextPage}>
              <i className="fas fa-angle-right" />
            </button>
          </li>

        </ul>
      </div>
    </div>
  );
};

export default Pagination;